<template>
  <b-container fluid>
    <h1 class="m-3">
      {{ title }}
    </h1>
    <b-row class="px-3 justify-content-between">
      <b-col cols="12">
        <div class="d-flex flex-wrap flex-lg-nowrap filter-gap">

          <div class="filter-width">
            <filter-site-picker :use-default-value="true" :multiple="true" :select-all="true" operator="equals"
              :item-key="siteIdFilterProperty" :item-label="siteLabelFilterProperty"
              :item-currency="siteCurrencyFilterProperty" :group-by-key="groupKey" :group-by-label="groupLabel"
              @filterWithIdChanged="siteFilterChanged($event)" />
          </div>

          <div class="d-flex filter-gap filter-width flex-wrap">
            <date-picker :title="$t('filter.day')" :date-filter-property="dateFilterProperty"
              :date-picker-format="datePickerFormat" @dateFiltersUpdated="dateFiltersUpdated($event)" />
          </div>

          <b-dropdown toggle-class="dropdown-style" right :text="$t('export.export')"
            class="ml-lg-auto align-self-center align-self-lg-start">
            <b-dropdown-item class="mb-2" :disabled="!dataReady" @click="downloadPdf()">
              <font-awesome-icon icon="file-pdf" class="mr-2 export-icon-pdf" />
              {{ $t("export.PDF") }}
            </b-dropdown-item>
            <b-dropdown-item class="mb-2" :disabled="!dataReady" @click="downloadExcel()">
              <font-awesome-icon icon="file-excel" class="mr-2 export-icon-excel" />
              {{ $t("export.excel") }}
            </b-dropdown-item>
            <b-dropdown-item :disabled="!dataReady" @click="downloadCsv()">
              <font-awesome-icon icon="file-csv" class="mr-2 export-icon" />
              {{ $t("export.csv") }}
            </b-dropdown-item>
          </b-dropdown>

        </div>
      </b-col>
    </b-row>
    <div v-for="(tables, index) in tablesData" :key="index">
      <table-renderer :data-table="tables.dataTable1.dataTable" :dimension-fields="dimensionColumnsTable1"
        :measure-fields="getMeasureColumnsTable1(tables)" :table-header="tables.dataTable1.tableHeader"
        :group-headers="groupHeadersTable1" :data-ready="dataReadyTables1" :custom-table-class="customTableClass"
        ref="tableRenderers1" />

      <table-renderer :data-table="tables.dataTable2.dataTable" :dimension-fields="dimensionColumnsTable2"
        :measure-fields="measureColumnsTable2" :table-header="tables.dataTable2.tableHeader"
        :group-headers="groupHeadersTable2" :totals-data="tables.dataTable2.totalsData" :data-ready="dataReadyTables2"
        :custom-table-class="customTableClass" ref="tableRenderers2" />

      <table-renderer :data-table="tables.dataTable3.dataTable" :measure-fields="getMeasureColumnsTable3(tables)"
        :table-header="tables.dataTable3.tableHeader" :group-headers="groupHeadersTable3" :data-ready="dataReadyTables3"
        :custom-table-class="customTableClass" ref="tableRenderers3" />

      <hr v-if="index < tablesData.length - 1" class="separator">
    </div>
  </b-container>
</template>

<script>
import moment from "moment";
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import TableRenderer from "@/components/TableRenderer.vue";
import dimensionEnum from "@/model/dimensionEnum";
import { dateFormatEnum } from "@/filter/dateFormat";
import paths from "@/router/pathConstants";
import FilterSitePicker from "@/components/picker/FilterSitePicker.vue";
import DatePicker from "@/components/picker/DatePicker.vue";

import { OperationsDaily } from "@/router/routeNames";
import { mapState } from "vuex";

export default {
  name: OperationsDaily,
  components: {
    TableRenderer,
    FilterSitePicker,
    DatePicker,
  },
  data() {
    return {
      /** Tableau d'objets des sites sélectionnés, indiquant leur id et leur label */
      siteValues: [],
      /** Filtre venant du FilterPicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "Site.label",
       *      operator: "equals",
       *      values: ["Site 1"]
       *    }
       */
      siteFilter: null,
      siteIdFilterProperty: dimensionEnum.SITE,
      siteLabelFilterProperty: dimensionEnum.SITE_NAME,
      siteCurrencyFilterProperty: dimensionEnum.SITE_CURRENCY,
      groupKey: dimensionEnum.SITE_SITE_CATEGORY,
      groupLabel: dimensionEnum.SITE_SITE_CATEGORY_NAME,
      useSiteFilterDefaultValue: true,
      /** Tableau contenant tous les filtres venant du DatePicker, tels qu'attendus par cubeJs.
       * Ex :
       * [
       *    {
       *      dimension: "BusinessDate.day",
       *      operator: "equals",
       *      values: [30]
       *    },
       *    ...
       * ]
       */
      dateFilters: null,
      /** Date sélectionnée, au format indiqué au DatePicker */
      pickedDate: "",
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      /** Libellé de la période sélectionnée */
      labelPeriod: "",
      dateFilterProperty: dimensionEnum.DATE,
      dimensionColumnsTable1: [],
    };
  },
  computed: {
    ...mapState({
      tablesData: (state) => state.operations.tablesData,
      dataReadyTables1: (state) => state.operations.dataReadyTables1,
      dataReadyTables2: (state) => state.operations.dataReadyTables2,
      dataReadyTables3: (state) => state.operations.dataReadyTables3,
      measureColumnsTable1: (state) => state.operations.measureColumnsTable1,
      measureColumnsTable2: (state) => state.operations.measureColumnsTable2,
      measureColumnsTable3: (state) => state.operations.measureColumnsTable3,
      dimensionColumnsTable2: (state) => state.operations.dimensionColmunTable2,
      groupHeadersTable1: (state) => state.operations.groupHeadersTable1,
      groupHeadersTable2: (state) => state.operations.groupHeadersTable2,
      groupHeadersTable3: (state) => state.operations.groupHeadersTable3,
    }),
    isDefaultQueryReady() {
      return this.siteFilter &&
        this.dateFilters &&
        this.dateFilters.length === 1
        ? true
        : false;
    },
    title() {
      return this.$t("title.operations_daily");
    },
    customTableClass() {
      return "table-width-medium";
    },
    /** Etat des données qui font appel à la base */
    queriedDataReady() {
      return this.dataReadyTables1 && this.dataReadyTables2;
    },
    dataReady() {
      return this.dataReadyTables1 && this.dataReadyTables2 && this.dataReadyTables3;
    }
  },
  watch: {
    queriedDataReady(newValue, oldValue) {
      if (newValue && !oldValue) this.$store.dispatch('operations/updateTables3');
    },
  },
  mounted() {
    this.$store.commit('operations/setIsOmniplay', false);
    this.$store.dispatch('operations/initDisplayTable1');
    this.$store.dispatch('operations/initDisplayTable2');
    this.$store.dispatch('operations/initDisplayTable3');
  },
  created() {
    // Si les données étaient déjà prêtes, on initialise la table, sinon le watcher s'en occupe
    if (this.queriedDataReady) this.$store.dispatch('operations/updateTables3');
  },
  methods: {
    siteFilterChanged(value) {
      this.siteValues = value.values;
      this.$store.commit("operations/setSiteValues", value.values);
      this.siteFilter = value.idFilter;
      this.updateTables();
    },
    dateFiltersUpdated(value) {
      this.dateFilters = value.filters;
      this.pickedDate = value.pickedDate;
      this.labelPeriod = value.labelPeriod;
      this.updateTables();
    },
    updateTables() {
      if (this.isDefaultQueryReady) {
        var filters = [this.siteFilter];

        // Convertir le filtre de date en timeDimension au lieu de le garder en filtre classique
        const dateFilter = this.dateFilters.find(f => f.dimension === dimensionEnum.DATE);
        if (dateFilter) {
          const dateValue = dateFilter.values[0];
          this.$store.commit("operations/setTimeDimension", {
            dimension: dimensionEnum.DATE,
            dateRange: [dateValue, dateValue],
          });
        } else {
          this.$store.commit("operations/setTimeDimension", null);
        }

        this.$store.commit("operations/setFilters", filters);
        this.$store.dispatch("operations/initTablesData");
      }
    },
    getMeasureColumnsTable1(table) {
      return table.dataTable1.dynamicMeasureColumnsTable ?? this.measureColumnsTable1;
    },
    getMeasureColumnsTable3(table) {
      return table.dataTable3.dynamicMeasureColumnsTable ?? this.measureColumnsTable3;
    },
    downloadPdf() {
      if (
        this.dataReadyTables1 &&
        this.dataReadyTables2 &&
        this.dataReadyTables3
      ) {
        this.storeExportData();
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel() {
      if (
        this.dataReadyTables1 &&
        this.dataReadyTables2 &&
        this.dataReadyTables3
      ) {
        this.storeExportData();
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv() {
      if (
        this.dataReadyTables1 &&
        this.dataReadyTables2 &&
        this.dataReadyTables3
      ) {
        this.storeExportData();
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData() {
      let datasForExport = [];
      this.tablesData.forEach((table, index) => {
        const renderer1 = this.$refs.tableRenderers1?.[index];
        const renderer2 = this.$refs.tableRenderers2?.[index];
        const renderer3 = this.$refs.tableRenderers3?.[index];

        const sortKey1 = renderer1?.sortedColumn || null;
        const sortKey2 = renderer2?.sortedColumn || null;
        const sortKey3 = renderer3?.sortedColumn || null;

        const data1 = TableTools.Helper.assembleExportDataTable(
          table.dataTable1.dataTable,
          this.dimensionColumnsTable1,
          this.measureColumnsTable1,
          this.groupHeadersTable1,
          table.dataTable1.tableHeader,
          sortKey1,
          null,
          null,
          table.dataTable1.tableNumber
        );
        datasForExport.push(data1);
        const data2 = TableTools.Helper.assembleExportDataTable(
          table.dataTable2.dataTable,
          this.dimensionColumnsTable2,
          this.measureColumnsTable2,
          this.groupHeadersTable2,
          table.dataTable2.tableHeader,
          sortKey2,
          null,
          table.dataTable2.totalsData,
          table.dataTable2.tableNumber
        );
        datasForExport.push(data2);
        const data3 = TableTools.Helper.assembleExportDataTable(
          table.dataTable3.dataTable,
          null,
          this.measureColumnsTable3,
          this.groupHeadersTable3,
          table.dataTable3.tableHeader,
          sortKey3,
          null,
          null,
          table.dataTable3.tableNumber
        );
        datasForExport.push(data3);
      });

      const dateForFilename = moment(this.pickedDate, this.datePickerFormat)
        .format(dateFormatEnum.DATE_FORMAT_FOR_FILENAME)
        .toString();

      const siteLabels = this.siteValues.map((site) => site.label);

      TableTools.Helper.storeExportData(
        datasForExport,
        this.title,
        Tools.StringHelper.constructExportSubtitles(
          siteLabels,
          this.labelPeriod
        ),
        Tools.StringHelper.constructExportFilename(
          this.title,
          siteLabels[0],
          dateForFilename
        )
      );
    },
  },
};
</script>
